import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  input,
  viewChild,
} from '@angular/core';

import { IconComponent } from '../icon/icon.component';
import { Check } from '../icon/icons';
import { RadioItem, UI_RADIO_GROUP, UI_RADIO_ITEM } from './radio-group.component';

export type RadioRowMarker = 'none' | 'origin' | 'destination';

/**
 * Fila de selección única (paradas, rutas, motivos). Va dentro de un `ui-radio-group`. El estado se
 * anuncia con `aria-checked`, y a la vista con borde y marca, nunca sólo con color.
 */
@Component({
  selector: 'ui-radio-row',
  imports: [IconComponent],
  template: `
    <button
      #control
      type="button"
      role="radio"
      class="row"
      [class.selected]="checked()"
      [class.disabled]="disabled()"
      [attr.aria-checked]="checked()"
      [attr.aria-disabled]="disabled() ? 'true' : null"
      [attr.tabindex]="tabbable() ? 0 : -1"
      (click)="group.select(this)"
    >
      @if (marker() !== 'none') {
        <span class="marker" [class.origin]="marker() === 'origin'" [class.destination]="marker() === 'destination'"></span>
      }
      <span class="text">
        <span class="label">{{ label() }}</span>
        @if (meta(); as metaText) {
          <span class="meta">{{ metaText }}</span>
        }
      </span>
      <span class="check" aria-hidden="true">
        @if (checked()) {
          <ui-icon [icon]="checkIcon" size="0.875rem" [strokeWidth]="3" />
        }
      </span>
    </button>
  `,
  styleUrl: './radio-row.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: UI_RADIO_ITEM, useExisting: forwardRef(() => RadioRowComponent) }],
})
export class RadioRowComponent implements RadioItem {
  // No es `required` a propósito: dentro de un @for, el grupo lee el valor de las hermanas antes de que
  // Angular les entregue el suyo, y un input requerido sin valor truena (NG0950).
  readonly value = input('');
  readonly label = input.required<string>();
  readonly meta = input<string>();
  readonly marker = input<RadioRowMarker>('none');
  readonly disabled = input(false, { transform: booleanAttribute });

  protected readonly group = inject(UI_RADIO_GROUP);
  protected readonly checkIcon = Check;
  protected readonly checked = computed(() => this.group.value() === this.value());
  protected readonly tabbable = computed(() => this.group.tabbable() === this.value());

  private readonly control = viewChild.required<ElementRef<HTMLButtonElement>>('control');

  focus(): void {
    this.control().nativeElement.focus();
  }
}
